/* eslint-disable no-prototype-builtins */
import {
  BI_ENDPOINT as BI_VIEWER_ENDPOIINT_PREVIEW,
  BI_VIEWER_ENDPOINT as BI_VIEWER_ENDPOINT_LIVE,
  BI_ERROR_ENDPOINT,
} from '@wix/wix-data-client-common-standalone'
import {
  Bi,
  Trace,
  Breadcrumb,
  DataBindingError,
  UserError,
  VerboseMessage,
  ConsoleEvent,
} from '../logger'
import { createErrorMonitor } from './logging/errorMonitor'

const { USER_SCOPE, APPLICATION_SCOPE, SERVER_SCOPE, CORE_SCOPE } = DataBindingError.scopes

const SENTRY_DSN_FOR_CORE_SCOPE_IN_PROD = 'https://0821d2edbaaf494aa59dc98cacbcb7af@o37417.ingest.us.sentry.io/139045'
const scopeToDsn = {
  [APPLICATION_SCOPE]: 'https://cef68e5a2fd04f08a7c2a02738dbc237@sentry-next.wixpress.com/2411',
  [USER_SCOPE]: 'https://e9289aa805f74cfabb806b3a206f4047@sentry-next.wixpress.com/10331',
  [SERVER_SCOPE]: 'https://e9c9bfc492a8457e8358d001a0958cb5@sentry-next.wixpress.com/10332',
  [CORE_SCOPE]: 'https://45f18e63f7064a63b07082d2bc09dfdc@sentry-next.wixpress.com/23244',
}
const SENTRY_DSN_FOR_UNHANDLED_PROMISE_REJECTIONS_IN_PROD = 'https://62f6a0a276194cb49cddd444a287aef2@sentry-next.wixpress.com/23630'

// unfortunately js decorators spec isn't stabilazed yet.
// so welcome debatable workaround
let loggingOnProd = function (Type, log) {
  return event => {
    if (this.mode.dev) {
      // TODO: add cli param for verbose logging i.e. if (verbose) console.log(event)
      if (Type === Error) {
        this.console.error(event)
        if (event.cause) {
          this.console.error('Caused by:', event.cause)
        }
      }

      return event.run?.()
    } else {
      return log(event)
    }
  }
}

export default class Logger {
  constructor({ fedops, bi, monitor, verbose, console, platform, createPanoramaClient }) {
    const consoleLogger = console.factory()

    loggingOnProd = loggingOnProd.bind({
      mode: platform.settings.mode,
      console: consoleLogger,
    })

    this._eventToHandler = this._createEventToHandler()
    this._fedOpsLogger = this._createFedopsLogger(fedops)
    this._bi = this._createBiLoggers({ bi, settings: platform.settings })

    this._monitor = this._setupMonitor({
      monitor,
      platform,
    })
    this._verboseLogger = verbose.factory()
    this._console = consoleLogger
    this._errorMonitorTags = {
      msid: platform.settings.metaSiteId,
      url: (platform.location.pageUrl || '').slice(0, 200),
      viewMode: platform.settings.mode.name,
      renderMode: platform.settings.env.name,
    }

    if (!platform.settings.env.editor) { // TODO: Consider finding a way to include the Editor, excluding localhost.
      // There’s no way to recognize localhost in the Editor because, unlike
      // when running on a site, the app script URL looks like a production one,
      // and the override is handled through a redirect.
      const underAutomatedTesting = platform.location.queryParams.enableTestApi === 'true'
      if (!underAutomatedTesting) {
        const { artifactVersion } = platform.settings
        const inProduction = /^\d+\.\d+\.\d+$/.test(artifactVersion)
        // Only if it’s a production version, not localhost or a pull request.
        if (inProduction) {
          const commonPanoramaClientOptions = {
            artifactVersion,
            componentId: 'cms-viewer-app',
            fullArtifactId: 'com.wixpress.dbsm.dbsm-viewer-app',
          }

          // TODO: Add integration tests—consider using the Panorama testkit.
          this._panoramaMonitor = createPanoramaClient({
            ...commonPanoramaClientOptions,
            sentryDsn: SENTRY_DSN_FOR_CORE_SCOPE_IN_PROD,
            sentryEnvironment: platform.settings.mode.name,
          }).errorMonitor()

          // The Panorama error monitor, unlike its now-deprecated predecessor,
          // implicitly reports unhandled promise rejections.
          //
          // However, we don't want to track these (see the comment in the
          // `DataBindingLogger` constructor in
          // `packages/dbsm-viewer-app/src/logger/index.js`).
          //
          // We were advised to use a workaround: instantiate a second Panorama
          // client (with a different Sentry DSN) for the same artifact. This
          // causes unhandled promise rejections to be implicitly reported only
          // to the second instance.
          //
          // See: https://wix.slack.com/archives/C03NY9ZL45R/p1758532500714649
          createPanoramaClient({
            ...commonPanoramaClientOptions,
            sentryDsn: SENTRY_DSN_FOR_UNHANDLED_PROMISE_REJECTIONS_IN_PROD,
          })
        }
      }
    }
  }

  log(...events) {
    if (events.length > 1) {
      return events.map(event => this.log(event))
    }

    const [event] = events
    const [, handle] =
      Array.from(this._eventToHandler).find(([Type]) =>
        Type.prototype.isPrototypeOf(event),
      ) || []

    return handle
      ? handle(event)
      : this._console.error(`This event is not supported by logger`, event)
  }

  _createEventToHandler() {
    return new Map([
      [
        Error,
        loggingOnProd(Error, error => {
          const {
            scope,
            cause,
            options: { extra, ...restOptions } = {},
          } = error

          if (error instanceof UserError) {
            this._console.error(error)
            if (cause) {
              this._console.error('Caused by:', cause)
            }
          }

          const hasCause = cause instanceof Error
          const errorToReport = hasCause ? cause : error
          const sentryData = {
            tags: {
              ...restOptions,
              ...this._errorMonitorTags,
              wrapperErrorMessage: hasCause
                ? `${error.name}: ${error.message}\n${error.stack}`.slice(
                  0,
                  200,
                )
                : undefined,
            },
            contexts: {
              ...extra,
              wrapperError: hasCause ? error : undefined,
            },
          }

          this._monitor[scope].captureException(errorToReport, sentryData)

          if (scope === CORE_SCOPE) {
            this._panoramaMonitor?.reportError(errorToReport, sentryData)
          }
        }),
      ],
      [
        Bi,
        loggingOnProd(Bi, ({ event, type }) =>
          type === Bi.types.ERROR
            ? this._bi.errorLogger.log(event)
            : this._bi.logger.log(event),
        ),
      ],
      [
        Trace,
        loggingOnProd(Trace, ({ name, params, run, onStart, onEnd }) => {
          onStart(() => this._fedOpsLogger.interactionStarted(name, params))
          onEnd(() => this._fedOpsLogger.interactionEnded(name, params))

          return run()
        }),
      ],
      [
        Breadcrumb,
        loggingOnProd(Breadcrumb, ({ event }) => {
          this._monitor[APPLICATION_SCOPE].addBreadcrumb(event)
          this._monitor[CORE_SCOPE].addBreadcrumb(event)
          this._panoramaMonitor?.addBreadcrumb(event)
        }),
      ],
      [VerboseMessage, ({ messages }) => this._verboseLogger.log(...messages)],
      [ConsoleEvent, ({ message, level }) => this._console[level](message)],
    ])
  }

  _createFedopsLogger({ factory, hooks: { start, end } }) {
    return factory.getLoggerForWidget({
      appId: 'databinding',
      appName: 'databinding',
      startHook: start,
      endHook: end,
      // widgetId: 'dataset',
    })
  }

  _createBiLoggers({ bi: { factory }, settings: { env } }) {
    return {
      logger: factory().logger({
        endpoint: env.editor
          ? BI_VIEWER_ENDPOIINT_PREVIEW
          : BI_VIEWER_ENDPOINT_LIVE,
      }),
      errorLogger: factory().logger({
        endpoint: BI_ERROR_ENDPOINT,
      }),
    }
  }

  _setupMonitor({ monitor, platform }) {
    return Object.entries(scopeToDsn).reduce((acc, [scope, dsn]) => {
      acc[scope] = createErrorMonitor({
        monitor,
        dsn,
        platform,
      })

      return acc
    }, {})
  }
}
